import React from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

const RefLink = ({ text, url }) => (
  <a href={url} target="_blank" rel="noreferrer" style={{
    display: 'block', marginTop: '0.5rem', padding: '0.5rem 0.75rem',
    background: 'rgba(26,122,76,0.04)', borderLeft: '3px solid var(--brand)',
    borderRadius: '0 4px 4px 0', fontSize: '0.8rem', lineHeight: '1.5',
    color: 'var(--fg)', textDecoration: 'none', transition: 'background 0.15s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'rgba(26,122,76,0.09)'}
    onMouseLeave={e => e.currentTarget.style.background = 'rgba(26,122,76,0.04)'}
    data-testid="ref-link"
  >
    <i className="fas fa-file-medical-alt" style={{ color: 'var(--brand)', marginRight: '0.4rem', fontSize: '0.75rem' }}></i>
    <span style={{ color: 'var(--fg)' }}>{text}</span>
    <i className="fas fa-external-link-alt" style={{ marginLeft: '0.4rem', fontSize: '0.65rem', color: 'var(--brand)', opacity: 0.7 }}></i>
  </a>
);

const History = () => {
  const { t } = useTranslation();

  const milestones = [
    {
      year: '2003', title: t('history.m2003t'), desc: t('history.m2003d'),
      refs: [
        { text: t('history.m2003r1'), url: t('history.m2003r1url') },
        { text: t('history.m2003r2'), url: t('history.m2003r2url') },
      ],
    },
    {
      year: '2010', title: t('history.m2010t'), desc: t('history.m2010d'),
      highlight: true,
      refs: [
        { text: t('history.m2010r1'), url: t('history.m2010r1url') },
      ],
    },
    {
      year: '2011', title: t('history.m2011t'), desc: t('history.m2011d'),
      featured: true,
      refs: [
        { text: t('history.m2011r1'), url: t('history.m2011r1url') },
        { text: t('history.m2011r2'), url: t('history.m2011r2url') },
      ],
    },
    {
      year: '2014', title: t('history.m2014t'), desc: t('history.m2014d'),
      refs: [
        { text: t('history.m2014r1'), url: t('history.m2014r1url') },
      ],
    },
    {
      year: '2015', title: t('history.m2015t'), desc: t('history.m2015d'),
      refs: [
        { text: t('history.m2015r1'), url: t('history.m2015r1url') },
      ],
    },
    {
      year: '2017', title: t('history.m2017t'), desc: t('history.m2017d'),
      refs: [
        { text: t('history.m2017r1'), url: t('history.m2017r1url') },
      ],
    },
    { year: '2018', title: t('history.m2018t'), desc: t('history.m2018d') },
    { year: '2019', title: t('history.m2019t'), desc: t('history.m2019d') },
    {
      year: '2020', title: t('history.m2020t'), desc: t('history.m2020d'),
      highlight: true,
    },
    { year: '2021', title: t('history.m2021t'), desc: t('history.m2021d') },
    { year: '2022', title: t('history.m2022t'), desc: t('history.m2022d') },
    { year: '2023', title: t('history.m2023t'), desc: t('history.m2023d') },
    { year: '2024', title: t('history.m2024t'), desc: t('history.m2024d') },
  ];

  return (
    <div data-testid="history-page" style={{ maxWidth: '820px', margin: '0 auto' }}>
      <SEO path="/history" title="History of AFHOS" description="From isolated efforts to a united African voice against hypertension. Explore the journey from AfSoH (2010) to AFHOS, including landmark academic milestones by Prof Dr Marc Twagirumukiza and colleagues." />
      <div className="page-header">
        <p className="overline">{t('history.overline')}</p>
        <h1 data-testid="history-title">{t('history.title')}</h1>
        <p>{t('history.subtitle')}</p>
      </div>

      <div className="content-section" style={{ marginBottom: '2.5rem' }}>
        <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: 'var(--fg)' }}>{t('history.intro')}</p>
      </div>

      <div style={{ position: 'relative', paddingLeft: '2rem' }}>
        {/* Vertical timeline line */}
        <div style={{
          position: 'absolute', left: '0.55rem', top: '0.5rem', bottom: '0.5rem',
          width: '2px', background: 'linear-gradient(180deg, var(--brand) 0%, var(--border) 100%)',
        }} />

        {milestones.map((m, i) => (
          <div key={i} data-testid={`milestone-${m.year}`} style={{
            position: 'relative', marginBottom: '2rem', paddingBottom: i < milestones.length - 1 ? '0' : '0',
          }}>
            {/* Timeline dot */}
            <div style={{
              position: 'absolute', left: '-1.72rem', top: '0.35rem',
              width: (m.highlight || m.featured) ? '14px' : '10px',
              height: (m.highlight || m.featured) ? '14px' : '10px',
              borderRadius: '50%',
              background: m.featured ? 'var(--brand)' : m.highlight ? 'var(--brand)' : '#fff',
              border: m.featured ? '2px solid var(--brand)' : '2px solid var(--brand)',
              boxShadow: m.featured ? '0 0 0 4px rgba(220,53,69,0.2)' : m.highlight ? '0 0 0 4px rgba(26,122,76,0.15)' : 'none',
            }} />

            <div style={{
              background: m.featured ? 'rgba(220,53,69,0.03)' : m.highlight ? 'rgba(26,122,76,0.03)' : 'transparent',
              border: m.featured ? '2px solid rgba(220,53,69,0.25)' : m.highlight ? '1px solid rgba(26,122,76,0.12)' : '1px solid transparent',
              borderLeft: m.featured ? '4px solid var(--brand)' : undefined,
              borderRadius: 'var(--radius)',
              padding: (m.highlight || m.featured) ? '1.25rem' : '0.25rem 0',
              boxShadow: m.featured ? '0 2px 12px rgba(220,53,69,0.08)' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.4rem' }}>
                <span style={{
                  fontFamily: 'var(--font-heading)', fontSize: '0.85rem', fontWeight: '700',
                  color: '#fff', background: 'var(--brand)', padding: '0.15rem 0.6rem',
                  borderRadius: '4px', letterSpacing: '0.02em',
                }}>
                  {m.year}
                </span>
                <h3 style={{ fontSize: '1.05rem', margin: 0, color: 'var(--fg)' }}>{m.title}</h3>
              </div>

              <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: '1.75', margin: '0.5rem 0' }}>
                {m.desc}
              </p>

              {m.refs && m.refs.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  {m.refs.map((ref, j) => (
                    <RefLink key={j} text={ref.text} url={ref.url} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default History;
